import React, { useRef } from "react";
import { ArrowLeftRight, Braces, Loader2, ScanText, Table2, Upload } from "lucide-react";
import { Card, CardTitle } from "campaign-builder/ui";
import { FieldError, RequiredLabel } from "../targeting/fields";
import { Dropdown, type DropdownOption } from "./Dropdown";
import type { VariableFileOption } from "./useTrackingConfig";

interface PlaceholdersCardProps {
  placeholders: string[];
  loading: boolean;
  variableFiles: VariableFileOption[];
  recipientsAmount: number;
  selectedFileId: string | null;
  columnMapping: Record<string, string>;
  fileError?: string | null;
  uploading?: boolean;
  onSelectFile: (fileId: string) => void;
  onMapColumn: (placeholder: string, column: string) => void;
  onUploadFile: (file: File) => void;
}

const ColumnIcon = () => <Table2 className="text-slate-400 h-4 w-4 shrink-0" />;

export const PlaceholdersCard: React.FC<PlaceholdersCardProps> = ({
  placeholders,
  loading,
  variableFiles,
  recipientsAmount,
  selectedFileId,
  columnMapping,
  fileError,
  uploading,
  onSelectFile,
  onMapColumn,
  onUploadFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedFile = variableFiles.find((f) => f.id === selectedFileId) ?? null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUploadFile(file);
  };

  const fileOptions: DropdownOption[] = variableFiles.map((file) => {
    const tooFewRows = file.rowsAmount < recipientsAmount;
    return {
      value: file.id,
      label: file.name,
      sublabel: tooFewRows ? `${file.rowsAmount} rows · fewer than your audience` : `${file.rowsAmount} rows`,
      disabled: tooFewRows,
    };
  });

  const columnOptions = (file: VariableFileOption): DropdownOption[] =>
    file.columns.map((col) => ({ value: col, label: col, icon: <ColumnIcon /> }));

  return (
    <Card
      className="flex flex-col gap-5 p-[25px]"
      data-targeting-section="placeholders"
      data-copilot-step="ADM Tracking"
    >
      <div className="flex flex-col gap-1">
        <CardTitle>Placeholders</CardTitle>
        <p className="text-muted-foreground text-sm leading-5">
          Any content within double brackets {"{{ }}"} in your design is treated as a placeholder. Each placeholder is
          replaced by a unique row from a .XLSX or .CSV file.
        </p>
      </div>

      <div className="border-border flex flex-col gap-3 rounded-lg border border-solid p-4">
        <div className="text-foreground flex items-center gap-2 text-sm font-medium">
          <ScanText className="h-4 w-4 text-muted-foreground" />
          {loading ? "Scanning your design…" : `${placeholders.length} placeholders were identified in your design.`}
        </div>
        {!loading && placeholders.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {placeholders.map((name) => (
              <span
                key={name}
                className="border-border text-slate-700 dark:text-slate-300 rounded-md border border-solid px-2 py-1 text-xs font-medium"
              >{`{{${name}}}`}</span>
            ))}
          </div>
        )}
      </div>

      {placeholders.length > 0 && (
        <div className="flex flex-col gap-2">
          <RequiredLabel>Choose a file to fill the placeholders</RequiredLabel>
          <div className="flex gap-2">
            <Dropdown
              value={selectedFileId}
              options={fileOptions}
              placeholder="Select a file from library"
              invalid={!!fileError}
              onSelect={onSelectFile}
              dataCy="admTrackingVariableFileSelect"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
              data-cy="admTrackingVariableFileInput"
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-cy="admTrackingUploadFileButton"
              className="text-foreground border-border flex h-10 shrink-0 items-center gap-2 rounded-lg border border-solid bg-background px-3 text-sm font-medium disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload new file"}
            </button>
          </div>
          <p className="text-muted-foreground text-xs leading-4">
            Ensure the number of rows in your file corresponds exactly to your audience size.
          </p>
          <FieldError message={fileError} />
        </div>
      )}

      {selectedFile && placeholders.length > 0 && (
        <div className="flex flex-col gap-2">
          <RequiredLabel>Link the placeholders to the appropriate column in the file.</RequiredLabel>
          <div className="flex flex-col gap-2">
            {placeholders.map((name) => (
              <div key={name} className="flex items-center gap-2">
                <div className="border-border text-foreground flex h-10 flex-1 items-center gap-2 rounded-lg border border-solid bg-background px-3 text-sm">
                  <Braces className="text-slate-400 h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{name}</span>
                </div>
                <ArrowLeftRight className="text-slate-400 h-4 w-4 shrink-0" />
                <div className="flex-1">
                  <Dropdown
                    value={columnMapping[name] ?? null}
                    options={columnOptions(selectedFile)}
                    placeholder="Select a column"
                    leftIcon={<ColumnIcon />}
                    onSelect={(column) => onMapColumn(name, column)}
                    dataCy={`admPlaceholderColumn-${name}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
